import {
  useState,
  useRef,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from "react"
import { View, ScrollView } from "react-native"
import { type NativeScrollEvent, type NativeSyntheticEvent } from "react-native"
import BFText from "@/components/standard/BFText"

const OPTION_HEIGHT = 46

export interface ScrollPickerOptionsProps {
  name: string
  value: string | number | null
  selected?: boolean
  textSizeCls?: string
}

export interface ScrollPickerProps {
  options: ScrollPickerOptionsProps[]
  value: string | number | null
  onValueChanged:
    | ((val: string | number | null) => void)
    | Dispatch<SetStateAction<string | number | null>>
}

const findIndex = (
  options: ScrollPickerOptionsProps[],
  value: string | number | null
): number => {
  let initialIdx = options.findIndex((option) => option.value === value)
  if (initialIdx < 0) {
    initialIdx = 0
  }
  return initialIdx
}

export default function ScrollPicker({
  options,
  value,
  onValueChanged,
}: ScrollPickerProps) {
  const scroll = useRef<ScrollView>(null)
  const [selectedIdx, setSelectedIdx] = useState<number>(
    findIndex(options, value)
  )

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const idx = Math.max(
      0,
      Math.min(
        options.length - 1,
        Math.round(event.nativeEvent.contentOffset.y / OPTION_HEIGHT)
      )
    )
    if (idx !== selectedIdx) {
      setSelectedIdx(idx)
      onValueChanged(options[idx].value)
    }
  }

  useEffect(() => {
    if (scroll.current) {
      const idx = findIndex(options, value)
      setTimeout(() => {
        scroll.current?.scrollTo({
          x: 0,
          y: idx * OPTION_HEIGHT,
          animated: false,
        })
      }, 1)
    }
  }, [])

  options.forEach((option, idx) => {
    option.selected = idx === selectedIdx

    let textSizeCls = "text-[12px]"
    const diff = Math.abs(idx - selectedIdx)
    if (idx === selectedIdx) {
      textSizeCls = "text-[20px]"
    } else if (diff === 1) {
      textSizeCls = "text-[16px]"
    } else if (diff === 2) {
      textSizeCls = "text-[14px]"
    }
    option.textSizeCls = textSizeCls
  })

  return (
    <View className="flex h-full flex-1 mx-4">
      <ScrollView
        className={`flex flex-1`}
        ref={scroll}
        onScroll={onScroll}
        scrollEventThrottle={4}
        decelerationRate="fast"
        showsVerticalScrollIndicator={false}
        snapToOffsets={options.reduce(
          (offsets) => {
            offsets.push(offsets[offsets.length - 1] + OPTION_HEIGHT)
            return offsets
          },
          [0]
        )}
      >
        <View className="h-[69px]" />
        {options.map((option: ScrollPickerOptionsProps, idx: number) => {
          return (
            <BFText
              className={`text-white font-figtree-bold text-center ${option.selected ? "opacity-100" : "opacity-50"} ${option.textSizeCls}`}
              style={{
                height: OPTION_HEIGHT,
                lineHeight: OPTION_HEIGHT,
              }}
              key={`scroll-item-${idx}`}
            >
              {option.name}
            </BFText>
          )
        })}
        <View className="h-[69px]" />
      </ScrollView>
      <View className="absolute h-px bg-white top-[69px] inset-x-[20px] opacity-50" />
      <View className="absolute h-px bg-white top-[112px] inset-x-[20px] opacity-50" />
    </View>
  )
}
